import axios from "axios";
import { useEffect } from "react";

import { CartService } from "@/components/Cart/CartService";
import { TBillingInterval } from "@/types/app";
import { TCheckoutType } from "../types.checkout";
import { useCheckoutSummaryQuery } from "./queries/useCheckoutSummaryQuery";

interface UseCartSummaryOptions {
  checkoutType: TCheckoutType;
  billingPeriod: TBillingInterval;
  enabled?: boolean;
}

/**
 * Watches CartService cart changes and fetches the order summary.
 * Delegates to useCheckoutSummaryQuery for caching — identical cart/codes
 * within 30s skip the network call. Syncs results back into CartService
 * so existing subscribers continue to work.
 */
export function useCartSummary({
  checkoutType,
  billingPeriod,
  enabled = true,
}: UseCartSummaryOptions) {
  const { summary, isLoading, isError, error } = useCheckoutSummaryQuery({
    checkoutType,
    billingPeriod,
    enabled,
  });

  useEffect(() => {
    if (isLoading) {
      CartService.setSummaryLoading();
      return;
    }
    if (isError) {
      CartService.setSummaryError(summaryErrorMessage(error));
      return;
    }
    if (summary) CartService.setSummaryData(summary);
  }, [isLoading, isError, error, summary]);
}

/**
 * The summary refuses a product the user already owns, which means the plan
 * changed after this page loaded (e.g. just paid on Stripe). A refresh rebuilds
 * the cart from the current plan.
 */
export function summaryErrorMessage(error: unknown): string {
  const message = axios.isAxiosError(error)
    ? error.response?.data?.error?.message
    : undefined;
  if (
    typeof message === "string" &&
    message.includes("is already in your subscription")
  ) {
    return "Your plan was just updated. Please refresh the page.";
  }
  return "Something went wrong";
}
